import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  ScrollArea,
  VisuallyHidden,
} from '@ais-ui/ui-core';
import React, { useEffect, useMemo, useState } from 'react';
import { useReleaseNotes } from '@ais-ui/api';
import {
  getNewestReleaseNotes,
  WordpressReleaseNote,
  parseDate,
} from '@ais-ui/utils';
import { ReleaseNotesContent } from './ReleaseNotesContent';
import { useReleaseNotesContext } from './ReleaseNotesProvider';
import { PiCaretLeft, PiCaretRight } from 'react-icons/pi';
import { ErrorDisplay } from '@ais-ui/ui-core';
import { useVersion } from '../../components/sidebar/useVersion';
import { useProfile } from '@ais-ui/profile';

// Extract semver version from release note title
function extractVersionFromTitle(title: string): string | null {
  const versionRegex = /(\d+\.\d+\.\d+(?:-\d+)?)/;
  const match = title.match(versionRegex);
  return match ? match[1] : null;
}

// Compare semver versions (returns true if version1 <= version2)
function isVersionLessOrEqual(version1: string, version2: string): boolean {
  const parseVersion = (v: string) => {
    const [main, hotfix] = v.split('-');
    const [major, minor, patch] = main.split('.').map(Number);
    return { major, minor, patch, hotfix: hotfix ? Number(hotfix) : 0 };
  };

  const v1 = parseVersion(version1);
  const v2 = parseVersion(version2);

  if (v1.major !== v2.major) return v1.major < v2.major;
  if (v1.minor !== v2.minor) return v1.minor < v2.minor;
  if (v1.patch !== v2.patch) return v1.patch < v2.patch;
  return v1.hotfix <= v2.hotfix;
}

function useHaveSeenReleaseNotes(
  releaseNotes: WordpressReleaseNote[],
  lastSeenCookieName: string,
) {
  const { username } = useProfile();

  const hasSeen = useMemo(() => {
    // Don't show release notes for test users (e.g., playwright1, playwright2, etc.)
    if (username.toLowerCase().includes('playwright')) {
      return true;
    }

    if (!releaseNotes || releaseNotes.length === 0) return true;

    // Get the newest release
    const newestRelease = getNewestReleaseNotes(releaseNotes);
    if (!newestRelease) return true;

    // Find the ID of the latest release seen by the user
    const newestSeenReleaseId: string | undefined = document.cookie
      .split('; ')
      .find((row) => row.startsWith(lastSeenCookieName))
      ?.split('=')[1];

    return Number(newestSeenReleaseId) === newestRelease.id;
  }, [releaseNotes, lastSeenCookieName, username]);

  return hasSeen;
}

export function ReleaseNotes() {
  const [shouldFetchReleaseNotes, setShouldFetchReleaseNotes] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { version: currentVersion } = useVersion();

  const { data, error } = useReleaseNotes(!shouldFetchReleaseNotes);

  const releaseNotes = useMemo(() => {
    if (!data) return [];

    // Filter release notes to only include versions with valid semver in title
    // and versions that are <= current version
    const filteredReleaseNotes = data.filter((releaseNote) => {
      const releaseVersion = extractVersionFromTitle(releaseNote.title);
      if (!releaseVersion) return false;

      // Only show if release version is <= current version
      return isVersionLessOrEqual(releaseVersion, currentVersion);
    });

    return filteredReleaseNotes.sort(
      (a, b) =>
        (parseDate(a.date)?.getTime() ?? 0) -
        (parseDate(b.date)?.getTime() ?? 0),
    );
  }, [data, currentVersion]);

  const currentReleaseNote = useMemo(
    () => releaseNotes?.[currentIndex] ?? null,
    [releaseNotes, currentIndex],
  );

  const lastSeenCookieName = 'lastSeenReleaseNotes';

  useEffect(() => {
    if (shouldFetchReleaseNotes && releaseNotes !== undefined) {
      setShouldFetchReleaseNotes(false);
    }
  }, [releaseNotes, shouldFetchReleaseNotes]);

  // Get the newest release notes
  const newestRelease = useMemo(
    () => getNewestReleaseNotes(releaseNotes),
    [releaseNotes],
  );

  useEffect(() => {
    if (newestRelease && releaseNotes) {
      // Find and set the index of the newest release
      const newestIndex = releaseNotes.findIndex(
        (releaseNote: WordpressReleaseNote) =>
          releaseNote.id === newestRelease.id,
      );
      setCurrentIndex(newestIndex >= 0 ? newestIndex : 0);
    }
  }, [newestRelease, releaseNotes]);

  const hasSeen = useHaveSeenReleaseNotes(releaseNotes, lastSeenCookieName);
  const { open, setOpen } = useReleaseNotesContext();

  useEffect(() => {
    if (!hasSeen && newestRelease) {
      setOpen(true);
    }
  }, [hasSeen, newestRelease, setOpen]);

  const handleClose = () => {
    setOpen(false);

    const date = new Date();
    date.setFullYear(date.getFullYear() + 10);

    if (!newestRelease) return;

    document.cookie =
      lastSeenCookieName +
      '=' +
      newestRelease.id +
      '; expires=' +
      date.toUTCString() +
      '; path=/' +
      '; SameSite=Lax';
  };

  const handlePrevious = () => {
    if (!releaseNotes || currentIndex <= 0) return;
    setCurrentIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    if (!releaseNotes || currentIndex >= releaseNotes.length - 1) return;
    setCurrentIndex((prev) => prev + 1);
  };

  if (error) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="p-4 lg:p-6 xl:p-10 flex flex-col !max-w-[650px] lg:!max-w-[750px] xl:!max-w-[1000px] max-h-[75%] bg-gradient-to-b from-background to-muted">
          <VisuallyHidden>
            <DialogTitle>Release notes</DialogTitle>
          </VisuallyHidden>
          <ErrorDisplay message="Unable to load release notes. Please try again later.">
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </ErrorDisplay>
        </DialogContent>
      </Dialog>
    );
  }

  if (!currentReleaseNote) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="p-4 lg:p-6 xl:p-10 flex flex-col !max-w-[650px] lg:!max-w-[750px] xl:!max-w-[1000px] max-h-[75%] bg-gradient-to-b from-background to-muted border-0 border-t-4 border-primary">
        <DialogHeader className="flex flex-row items-center justify-between">
          <div className="flex-1">
            <DialogTitle className="mb-2">
              {currentReleaseNote.title}
            </DialogTitle>
            <DialogDescription>{currentReleaseNote.subtitle}</DialogDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePrevious}
              disabled={currentIndex <= 0}
              className="size-8"
            >
              <PiCaretLeft className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNext}
              disabled={
                !releaseNotes || currentIndex >= releaseNotes.length - 1
              }
              className="size-8"
            >
              <PiCaretRight className="size-4" />
            </Button>
          </div>
        </DialogHeader>
        <ScrollArea
          className="h-[300px] lg:h-[400px] xl:h-[450px]"
          observeResize={false}
        >
          <ReleaseNotesContent releaseNotes={currentReleaseNote} />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
